import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Button,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Chip,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Stack,
  InputAdornment,
} from "@mui/material";
import Grid from '@mui/material/Grid2';
import DataGrid from './ui/DataTable';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import StatusDonut from './ui/StatusDonut';
import dayjs from 'dayjs';
import axiosInstance from './axiosInstance';
import { useTheme, alpha } from '@mui/material/styles';
import { isValidEmail } from "../utils/validation";
import { useSnackbar } from "../providers/SnackbarProvider";
import SectionCard from './ui/SectionCard';
import EmptyState from './ui/EmptyState';
import { usePipelineConfig } from '../providers/PipelineConfigProvider';
import { useBreadcrumbs } from './BreadcrumbBar';
import useIsManager from '../hooks/useIsManager';

// Auswahloptionen (deckungsgleich mit den Model-Choices im Backend)
const STATUS_OPTIONS = [
  { value: 'NEW', label: 'Neu' },
  { value: 'IN_REVIEW', label: 'In Prüfung' },
  { value: 'PROPOSED', label: 'Vorgeschlagen' },
  { value: 'REJECTED', label: 'Abgelehnt' },
];
const CATEGORY_OPTIONS = [
  { value: 'partner', label: 'Partner' },
  { value: 'new', label: 'Neu' },
  { value: 'unknown', label: 'Unbekannt' },
];
const LEAD_SCORE_OPTIONS = [
  { value: 'LOW', label: 'Niedrig' },
  { value: 'MEDIUM', label: 'Mittel' },
  { value: 'QUALIFIED', label: 'Qualifiziert' },
  { value: 'HIGH', label: 'Hoch' },
  { value: 'TOP_OPPORTUNITY', label: 'Top Opportunity' },
];
const SKILL_OPTIONS = [
  { value: 'PERFECT', label: 'Perfekt' },
  { value: 'GOOD', label: 'Gut' },
  { value: 'PARTIAL', label: 'Teilweise' },
];
const CONSULTANT_TYPE_OPTIONS = [
  { value: 'INTERNAL', label: 'Intern' },
  { value: 'FREELANCER', label: 'Freelancer' },
  { value: 'PARTNER', label: 'Partner' },
];

const STATUS_LABELS = Object.fromEntries(STATUS_OPTIONS.map((o) => [o.value, o.label]));
const CONSULTANT_TYPE_LABELS = Object.fromEntries(CONSULTANT_TYPE_OPTIONS.map((o) => [o.value, o.label]));

const EMPTY_SUBMISSION = {
  recruiting_agency: '', salutation: '', contact_person: '', email: '', phone: '',
  agency_category: '', position_requested: '', technology: '', end_client: '',
  project_location: '', request_date: '', project_start_date: '',
  project_duration_months: '', remote_percentage: '', remote_rate: '', onsite_rate: '',
  proposed_consultant: '', consultant_type: '', skill_match_score: '',
  request_status: 'NEW', lead_score: '', notes: '',
};

const initials = (name) =>
  (name || '?').trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

// Leerzustand bei Filter/Suche ohne Treffer (D4) — Slots erwarten Komponenten
const NoResults = () => (
  <EmptyState title="Nichts gefunden." hint="Such- oder Filterkriterien anpassen." />
);

const daysSince = (dateStr) => {
  if (!dateStr) return null;
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  return isNaN(days) ? null : Math.max(0, days);
};

const euro = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  if (isNaN(n)) return null;
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
};

// Leere Strings → null, damit das Backend Datums-/Zahlenfelder akzeptiert
const sanitize = (d) => {
  const nn = (v) => (v === '' || v === undefined ? null : v);
  return {
    ...d,
    request_date: nn(d.request_date),
    project_start_date: nn(d.project_start_date),
    project_duration_months: nn(d.project_duration_months),
    remote_percentage: nn(d.remote_percentage),
    remote_rate: nn(d.remote_rate),
    onsite_rate: nn(d.onsite_rate),
  };
};

function AgencySubmissionList() {
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const isManager = useIsManager();
  // Stale-Schwellen aus der Pipeline-Konfiguration (B3)
  const { staleHintDays, staleWarnDays } = usePipelineConfig();

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newSubmission, setNewSubmission] = useState(EMPTY_SUBMISSION);
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  useBreadcrumbs([{ label: 'Agenturanfragen' }]);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await axiosInstance.get('/agency-submissions/');
        setSubmissions(response.data);
      } catch (error) {
        console.error('Error fetching agency submissions', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ---------- Farben ----------
  // Chips: Reihenfolge in Ramp-Stufen, Vorgeschlagen grün, Abgelehnt rot
  const statusColor = (status) => ({
    NEW: theme.palette.tokens.ramp[500],
    IN_REVIEW: theme.palette.tokens.ramp[700],
    PROPOSED: theme.palette.success.main,
    REJECTED: theme.palette.error.main,
  }[status] || theme.palette.text.secondary);
  // Donut: bleibt komplett in der Indigo-Familie (kein Grün neben Lila) —
  // hell = früh, dunkel = weit, Abgelehnt neutralgrau
  const statusChartColor = (status) => ({
    NEW: theme.palette.tokens.ramp[200],
    IN_REVIEW: theme.palette.tokens.ramp[500],
    PROPOSED: theme.palette.tokens.ramp[700],
    REJECTED: theme.palette.tokens.chartNeutral,
  }[status] || theme.palette.tokens.chartNeutral);

  const skillColor = (skill) => ({
    PERFECT: theme.palette.statusColors.green,
    GOOD: theme.palette.statusColors.blue,
    PARTIAL: theme.palette.statusColors.orange,
  }[skill] || theme.palette.text.secondary);

  const scoreColor = (score) => ({
    LOW: theme.palette.leadScore.cold,
    MEDIUM: theme.palette.leadScore.cool,
    QUALIFIED: theme.palette.leadScore.qualified,
    HIGH: theme.palette.leadScore.warm,
    TOP_OPPORTUNITY: theme.palette.leadScore.hot,
  }[score] || theme.palette.text.secondary);

  const softChip = (label, color, extra = {}) => (
    <Chip size="small" label={label} sx={{ bgcolor: alpha(color, 0.13), color, fontWeight: 600, ...extra }} />
  );

  // ---------- Kennzahlen ----------
  const metrics = useMemo(() => {
    const counts = submissions.reduce((acc, s) => {
      acc[s.request_status] = (acc[s.request_status] || 0) + 1;
      return acc;
    }, {});
    const statusDistribution = Object.entries(counts).map(([status, value]) => ({
      id: status, label: STATUS_LABELS[status] || 'Unbekannt', value, color: statusChartColor(status),
    }));
    const openCount = (counts.NEW || 0) + (counts.IN_REVIEW || 0);
    const proposedRate = submissions.length ? Math.round(((counts.PROPOSED || 0) / submissions.length) * 100) : 0;
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recent = submissions.filter((s) => new Date(s.created_at) >= thirtyDaysAgo).length;
    return { statusDistribution, openCount, proposedRate, recent };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissions]);

  // ---------- Filter ----------
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return submissions.filter((s) => {
      if (statusFilter !== 'ALL' && s.request_status !== statusFilter) return false;
      if (!q) return true;
      return [s.recruiting_agency, s.contact_person, s.position_requested, s.end_client, s.proposed_consultant, s.technology]
        .some((f) => (f || '').toLowerCase().includes(q));
    });
  }, [submissions, search, statusFilter]);

  // ---------- Leerzustände (D4) ----------
  // Suche/Status-Filter laufen außerhalb der Grid — bei aktivem Filter ohne
  // Treffer sieht die Grid daher ebenfalls „keine Zeilen". NoRows unterscheidet
  // deshalb selbst zwischen echter Leere und leergefiltertem Bestand.
  const NoRows = useCallback(() => (
    submissions.length ? <NoResults /> : (
      <EmptyState
        title="Keine Agenturanfragen."
        hint="Neue Anfragen landen automatisch hier."
      />
    )
  ), [submissions.length]);

  // ---------- Zellen ----------
  const renderRequestCell = (params) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, height: '100%', minWidth: 0 }}>
      <Avatar sx={{ width: 32, height: 32, fontSize: '0.78rem', fontWeight: 700, bgcolor: 'primary.light', color: 'primary.dark', flex: 'none' }}>
        {initials(params.row.recruiting_agency)}
      </Avatar>
      <Box sx={{ minWidth: 0, lineHeight: 1.3 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{params.row.recruiting_agency}</Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {params.row.position_requested || '—'}
        </Typography>
      </Box>
    </Box>
  );

  // Alter der Anfrage — offene Anfragen veralten schnell (Stale-Färbung ab 7/14 Tagen)
  const renderAgeCell = (params) => {
    const { row } = params;
    const days = daysSince(row.request_date || row.created_at);
    if (days === null) return <Typography variant="caption" color="text.disabled">—</Typography>;
    const isOpen = row.request_status === 'NEW' || row.request_status === 'IN_REVIEW';
    const color = isOpen && days >= staleWarnDays ? 'error.main'
      : isOpen && days >= staleHintDays ? 'warning.main' : 'text.secondary';
    const weight = isOpen && days >= staleHintDays ? 700 : 400;
    return (
      <Typography variant="body2" sx={{ color, fontWeight: weight, fontVariantNumeric: 'tabular-nums' }}>
        {days === 0 ? 'heute' : `${days} ${days === 1 ? 'Tag' : 'Tage'}`}
      </Typography>
    );
  };

  const columns = [
    {
      field: 'recruiting_agency', headerName: 'Anfrage', minWidth: 230, flex: 1.5,
      renderCell: renderRequestCell,
    },
    {
      field: 'request_status', headerName: 'Status', minWidth: 130, flex: 0.8,
      renderCell: (params) => params.value
        ? softChip(STATUS_LABELS[params.value] || params.value, statusColor(params.value))
        : <Typography variant="caption" color="text.disabled">—</Typography>,
    },
    {
      field: 'skill_match_score', headerName: 'Skill-Match', minWidth: 110, flex: 0.65,
      renderCell: (params) => params.value
        ? softChip(SKILL_OPTIONS.find((o) => o.value === params.value)?.label || params.value, skillColor(params.value))
        : <Typography variant="caption" color="text.disabled">—</Typography>,
    },
    {
      field: 'lead_score', headerName: 'Score', minWidth: 120, flex: 0.7,
      renderCell: (params) => params.value
        ? softChip(params.value === 'TOP_OPPORTUNITY' ? 'TOP' : (LEAD_SCORE_OPTIONS.find((o) => o.value === params.value)?.label || params.value), scoreColor(params.value))
        : <Typography variant="caption" color="text.disabled">—</Typography>,
    },
    {
      field: 'request_date', headerName: 'Eingegangen vor', minWidth: 120, flex: 0.7,
      valueGetter: (value, row) => daysSince(row.request_date || row.created_at) ?? -1,
      renderCell: renderAgeCell,
    },
    {
      field: 'end_client', headerName: 'Endkunde', minWidth: 130, flex: 0.9,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" noWrap>{params.value || '—'}</Typography>
      ),
    },
    {
      field: 'proposed_consultant', headerName: 'Berater', minWidth: 140, flex: 0.9,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', minWidth: 0, lineHeight: 1.3 }}>
          <Typography variant="body2" sx={{ color: params.value ? 'text.primary' : 'text.disabled' }} noWrap>{params.value || '—'}</Typography>
          {params.row.consultant_type && (
            <Typography variant="caption" color="text.secondary" noWrap>{CONSULTANT_TYPE_LABELS[params.row.consultant_type]}</Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'remote_rate', headerName: 'Satz (remote)', minWidth: 110, flex: 0.6, align: 'right', headerAlign: 'right',
      valueGetter: (value) => (value === null || value === undefined || value === '' ? -1 : Number(value)),
      renderCell: (params) => {
        const formatted = euro(params.row.remote_rate);
        return formatted
          ? <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatted}</Typography>
          : <Typography variant="caption" color="text.disabled">—</Typography>;
      },
    },
    // Löschen ist serverseitig Managern vorbehalten (Security S3) —
    // Spalte daher nur für Manager einblenden.
    ...(isManager ? [{
      field: 'actions', headerName: '', width: 56, sortable: false, disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Tooltip title="Löschen">
            <IconButton
              variant="action-delete" size="small" className="row-delete"
              sx={{ opacity: 0.25, transition: 'opacity .15s' }}
              onClick={(e) => { e.stopPropagation(); handleDelete(params.row.id); }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    }] : []),
  ];

  // ---------- Anlegen / Bearbeiten / Löschen ----------
  const handleCreate = async () => {
    if (!newSubmission.recruiting_agency.trim()) {
      showSnackbar('Agentur ist erforderlich', 'error');
      return;
    }
    if (newSubmission.email && !isValidEmail(newSubmission.email)) {
      showSnackbar('Ungültige E-Mail-Adresse', 'error');
      return;
    }
    try {
      const response = await axiosInstance.post('/agency-submissions/', sanitize(newSubmission));
      setSubmissions([response.data, ...submissions]);
      setCreateDialogOpen(false);
      setNewSubmission(EMPTY_SUBMISSION);
      showSnackbar('Agenturanfrage erfolgreich erstellt', 'success');
    } catch (error) {
      console.error('Error creating agency submission:', error);
      showSnackbar('Fehler beim Erstellen', 'error');
    }
  };

  const handleSaveEdit = async () => {
    if (selected.email && !isValidEmail(selected.email)) {
      showSnackbar('Ungültige E-Mail-Adresse', 'error');
      return;
    }
    try {
      const response = await axiosInstance.put(`/agency-submissions/${selected.id}/`, sanitize(selected));
      setSubmissions((prev) => prev.map((row) => (row.id === selected.id ? response.data : row)));
      setEditOpen(false);
      setSelected(null);
      showSnackbar('Agenturanfrage erfolgreich aktualisiert', 'success');
    } catch (error) {
      console.error('Error updating agency submission:', error);
      showSnackbar('Fehler beim Speichern', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie diese Agenturanfrage wirklich löschen?')) return;
    try {
      await axiosInstance.delete(`/agency-submissions/${id}/`);
      setSubmissions((prev) => prev.filter((c) => c.id !== id));
      showSnackbar('Agenturanfrage erfolgreich gelöscht', 'success');
    } catch (error) {
      console.error('Error deleting agency submission:', error);
      showSnackbar('Fehler beim Löschen', 'error');
    }
  };

  // ---------- Gemeinsames Formular (Anlegen + Bearbeiten) ----------
  // Pillen-Auswahl für die Bewertung: sichtbarer als Dropdowns, Farbe = Bedeutung.
  // Erneuter Klick auf die aktive Pille wählt ab (außer bei Pflichtauswahl).
  const pillSelect = (label, value, options, onSelect, colorFor, allowEmpty = true) => (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>{label}</Typography>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
        {options.map((o) => {
          const active = value === o.value;
          const color = colorFor ? colorFor(o.value) : theme.palette.primary.main;
          return (
            <Box
              key={o.value} role="button" tabIndex={0} aria-pressed={active}
              onClick={() => onSelect(active && allowEmpty ? '' : o.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(active && allowEmpty ? '' : o.value); } }}
              sx={{
                px: 1.5, py: 0.5, borderRadius: 999, fontSize: '0.8rem', fontWeight: 600,
                cursor: 'pointer', userSelect: 'none', transition: 'background-color .15s, color .15s, box-shadow .15s',
                color: active ? color : 'text.secondary',
                bgcolor: active ? alpha(color, 0.15) : 'action.hover',
                boxShadow: active ? `inset 0 0 0 1.5px ${alpha(color, 0.5)}` : 'none',
                '&:hover': { bgcolor: active ? alpha(color, 0.22) : 'action.selected' },
                '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.main}`, outlineOffset: 1 },
              }}
            >
              {o.label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  const renderForm = (data, setField) => {
    const field = (name, label, extra = {}) => (
      <TextField
        name={name} label={label} value={data[name] ?? ''} fullWidth
        onChange={(e) => setField(name, e.target.value)}
        {...extra}
      />
    );
    const selectField = (name, label, options, withEmpty = true) => (
      <TextField
        select name={name} label={label} value={data[name] ?? ''} fullWidth
        onChange={(e) => setField(name, e.target.value)}
      >
        {withEmpty && <MenuItem value=""><em>Keine Auswahl</em></MenuItem>}
        {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
      </TextField>
    );
    const dateField = (name, label) => field(name, label, { type: 'date', InputLabelProps: { shrink: true } });
    // Zwei kurze Felder nebeneinander (auf Mobil untereinander)
    const pair = (a, b) => (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>{a}</Grid>
        <Grid size={{ xs: 12, sm: 6 }}>{b}</Grid>
      </Grid>
    );
    const sectionHead = (label, first = false) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: first ? 0.5 : 3.5, mb: 1.75 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 550, fontSize: '0.82rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}>{label}</Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
      </Box>
    );

    return (
      <Box>
        {sectionHead('Agentur & Kontakt', true)}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {pair(
            field('recruiting_agency', 'Agentur', { required: true }),
            selectField('agency_category', 'Kategorie', CATEGORY_OPTIONS)
          )}
          {pair(
            selectField('salutation', 'Anrede', [
              { value: 'Herr', label: 'Herr' }, { value: 'Frau', label: 'Frau' }, { value: 'Divers', label: 'Divers' },
            ]),
            field('contact_person', 'Ansprechpartner')
          )}
          {pair(field('email', 'E-Mail', { type: 'email' }), field('phone', 'Telefon'))}
        </Box>

        {sectionHead('Anfrage')}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {field('position_requested', 'Gesuchte Position')}
          {field('technology', 'Technologie / Skills')}
          {pair(field('end_client', 'Endkunde'), field('project_location', 'Projektort'))}
          {pair(dateField('request_date', 'Eingegangen am'), dateField('project_start_date', 'Projektstart'))}
          {pair(
            field('project_duration_months', 'Dauer', { type: 'number', InputProps: { endAdornment: <InputAdornment position="end">Monate</InputAdornment> } }),
            field('remote_percentage', 'Remote-Anteil', { type: 'number', InputProps: { endAdornment: <InputAdornment position="end">%</InputAdornment> } })
          )}
        </Box>

        {sectionHead('Konditionen')}
        {pair(
          field('remote_rate', 'Tagessatz remote', { type: 'number', InputProps: { endAdornment: <InputAdornment position="end">€ / Tag</InputAdornment> } }),
          field('onsite_rate', 'Tagessatz vor Ort', { type: 'number', InputProps: { endAdornment: <InputAdornment position="end">€ / Tag</InputAdornment> } })
        )}

        {sectionHead('Bewertung')}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {pillSelect('Status', data.request_status ?? 'NEW', STATUS_OPTIONS, (v) => setField('request_status', v), statusColor, false)}
          {pillSelect('Score', data.lead_score ?? '', LEAD_SCORE_OPTIONS, (v) => setField('lead_score', v), scoreColor)}
          {pillSelect('Skill-Match', data.skill_match_score ?? '', SKILL_OPTIONS, (v) => setField('skill_match_score', v), skillColor)}
          {pair(
            field('proposed_consultant', 'Vorgeschlagener Berater'),
            selectField('consultant_type', 'Beratertyp', CONSULTANT_TYPE_OPTIONS)
          )}
        </Box>

        {sectionHead('Notizen')}
        {field('notes', 'Notizen', { multiline: true, rows: 3 })}
      </Box>
    );
  };

  // ---------- Kacheln ----------
  const tile = (children) => (
    <SectionCard sx={{ height: 180, display: 'flex', flexDirection: 'column' }}>{children}</SectionCard>
  );

  const statusFilters = [{ key: 'ALL', label: 'Alle' }, ...STATUS_OPTIONS.map((o) => ({ key: o.value, label: o.label }))];

  return (
    <>
      <Grid container spacing={2.5}>
        {/* Kopf */}
        <Grid size={12}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2}>
            <Box>
              <Typography variant="h4" component="h1">Agenturanfragen</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {filtered.length} {filtered.length === 1 ? 'Anfrage' : 'Anfragen'}
                {(search || statusFilter !== 'ALL') ? ` (von ${submissions.length})` : ''}
              </Typography>
            </Box>
            <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)} sx={{ alignSelf: { xs: 'flex-start', md: 'auto' } }}>
              Neue Anfrage
            </Button>
          </Stack>
        </Grid>

        {/* Kennzahlen */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {tile(
            <>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 550, fontSize: '0.82rem', lineHeight: 1.5 }}>Statusverteilung</Typography>
              <StatusDonut data={metrics.statusDistribution} centerLabel="Anfragen" />
            </>
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {tile(
            <>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 550, fontSize: '0.82rem', lineHeight: 1.5 }}>Offene Anfragen</Typography>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', fontVariantNumeric: 'tabular-nums' }}>
                  {metrics.openCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">neu oder in Prüfung</Typography>
              </Box>
            </>
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {tile(
            <>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 550, fontSize: '0.82rem', lineHeight: 1.5 }}>Vorschlagsquote</Typography>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', fontVariantNumeric: 'tabular-nums' }}>
                  {metrics.proposedRate}%
                </Typography>
                <Typography variant="body2" color="text.secondary">Berater vorgeschlagen</Typography>
              </Box>
            </>
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {tile(
            <>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 550, fontSize: '0.82rem', lineHeight: 1.5 }}>Zugang · 30 Tage</Typography>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', fontVariantNumeric: 'tabular-nums' }}>
                  {metrics.recent}
                </Typography>
                <Typography variant="body2" color="text.secondary">neue Anfragen</Typography>
              </Box>
            </>
          )}
        </Grid>

        {/* Suche + stille Status-Filter */}
        <Grid size={12}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
            <TextField
              size="small" placeholder="Agentur, Position, Endkunde, Berater …" value={search} onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 280, maxWidth: 420, bgcolor: 'background.paper' }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="disabled" /></InputAdornment> }}
            />
            <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
              {statusFilters.map((f) => {
                const active = statusFilter === f.key;
                return (
                  <Box
                    key={f.key} role="button" tabIndex={0}
                    onClick={() => setStatusFilter(f.key)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setStatusFilter(f.key); } }}
                    sx={{
                      fontSize: '0.8rem', fontWeight: 600, px: active ? 1.25 : 0.75, py: 0.3, borderRadius: 999,
                      cursor: 'pointer', userSelect: 'none',
                      color: active ? 'primary.main' : 'text.disabled',
                      bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                      '&:hover': { color: active ? 'primary.main' : 'text.secondary' },
                      '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.main}`, outlineOffset: 1 },
                    }}
                  >
                    {f.label}
                  </Box>
                );
              })}
            </Box>
          </Stack>
        </Grid>

        {/* Tabelle als Fläche */}
        <Grid size={12}>
          <SectionCard sx={{ p: 0, overflow: 'hidden' }}>
            <DataGrid
              rows={filtered}
              columns={columns}
              loading={loading}
              getRowId={(row) => row.id}
              onRowClick={(params) => { setSelected({ ...params.row }); setEditOpen(true); }}
              disableRowSelectionOnClick
              autoHeight
              rowHeight={56}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRows, noResultsOverlay: NoResults }}
              sx={{
                border: 'none',
                '& .MuiDataGrid-row': { cursor: 'pointer' },
                '& .MuiDataGrid-row:hover .row-delete': { opacity: 1 },
                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
              }}
            />
          </SectionCard>
        </Grid>
      </Grid>

      {/* Anlegen */}
      <Dialog open={createDialogOpen} onClose={() => { setCreateDialogOpen(false); setNewSubmission(EMPTY_SUBMISSION); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          Neue Agenturanfrage
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Nur die Agentur ist Pflicht — alles Weitere lässt sich später ergänzen.
          </Typography>
        </DialogTitle>
        <DialogContent>
          {renderForm(newSubmission, (name, value) => setNewSubmission((prev) => ({ ...prev, [name]: value })))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setCreateDialogOpen(false); setNewSubmission(EMPTY_SUBMISSION); }}>Abbrechen</Button>
          <Button onClick={handleCreate} variant="contained" disabled={!newSubmission.recruiting_agency.trim()}>Erstellen</Button>
        </DialogActions>
      </Dialog>

      {/* Bearbeiten (Zeilenklick) — Kopf wie ein Datensatz: Avatar, Anfrage, Live-Status */}
      <Dialog open={editOpen} onClose={() => { setEditOpen(false); setSelected(null); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Avatar sx={{ width: 44, height: 44, fontSize: '0.95rem', fontWeight: 700, bgcolor: 'primary.light', color: 'primary.dark', flex: 'none' }}>
              {initials(selected?.recruiting_agency)}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h6" noWrap sx={{ lineHeight: 1.25 }}>{selected?.recruiting_agency || 'Agenturanfrage'}</Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {selected?.position_requested || 'Agenturanfrage bearbeiten'}
              </Typography>
            </Box>
            {selected?.request_status && (
              <Chip size="small" label={STATUS_LABELS[selected.request_status] || selected.request_status}
                sx={{ bgcolor: alpha(statusColor(selected.request_status), 0.13), color: statusColor(selected.request_status), fontWeight: 600, flex: 'none' }} />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selected && renderForm(selected, (name, value) => setSelected((prev) => ({ ...prev, [name]: value })))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {selected && (
            <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
              {[
                selected.created_at ? `Erstellt ${dayjs(selected.created_at).format('DD.MM.YYYY')}` : null,
                selected.last_update ? `zuletzt geändert ${dayjs(selected.last_update).format('DD.MM.YYYY, HH:mm')}` : null,
              ].filter(Boolean).join(' · ')}
            </Typography>
          )}
          <Button onClick={() => { setEditOpen(false); setSelected(null); }}>Abbrechen</Button>
          <Button onClick={handleSaveEdit} variant="contained">Speichern</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AgencySubmissionList;
